import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, MessageCircle, CreditCard, Banknote, Smartphone, FileText, ArrowRightLeft, Eye, Crown, Trash2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

const STATUS_LABELS = {
  pendente: { label: "Pendente", color: "#CBA871", bg: "#FEF3C7" },
  parcial: { label: "Parcial", color: "#7C2D12", bg: "#FED7AA" },
  pago: { label: "Pago", color: "#166534", bg: "#D1FAE5" },
  atrasado: { label: "Atrasado", color: "#991B1B", bg: "#FEE2E2" },
};

const PAYMENT_METHODS = [
  { value: "dinheiro", label: "Dinheiro", icon: Banknote },
  { value: "pix", label: "PIX", icon: Smartphone },
  { value: "credito", label: "Cartão de Crédito", icon: CreditCard },
  { value: "debito", label: "Cartão de Débito", icon: CreditCard },
  { value: "transferencia", label: "Transferência", icon: ArrowRightLeft },
  { value: "boleto", label: "Boleto", icon: FileText },
  { value: "fiado", label: "Fiado", icon: FileText },
];

const PM_LABEL = Object.fromEntries(PAYMENT_METHODS.map((p) => [p.value, p.label]));

export default function Invoices() {
  const { apiCall, user } = useAuth();
  const isMaster = user?.role === "master";
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ client_id: "", amount: "", due_date: "", description: "" });
  const [paymentForm, setPaymentForm] = useState({ amount: "", payment_method: "dinheiro", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [iRes, cRes] = await Promise.all([
        apiCall("get", "/invoices"),
        apiCall("get", "/clients"),
      ]);
      setInvoices(iRes.data);
      setClients(cRes.data);
    } catch {
      toast.error("Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiCall("post", "/invoices", {
        client_id: form.client_id,
        amount: parseFloat(form.amount),
        due_date: new Date(form.due_date).toISOString(),
        description: form.description,
      });
      toast.success("Cobrança criada");
      setOpen(false);
      setForm({ client_id: "", amount: "", due_date: "", description: "" });
      load();
    } catch {
      toast.error("Erro ao salvar");
    }
  };

  const handleOpenPayment = (inv) => {
    if (!isMaster) {
      toast.error("Apenas usuários master podem registrar pagamentos");
      return;
    }
    setSelected(inv);
    const remaining = inv.amount - (inv.paid_amount || 0);
    setPaymentForm({ amount: remaining.toFixed(2), payment_method: "dinheiro", notes: "" });
    setPaymentOpen(true);
  };

  const handleRegisterPayment = async (e) => {
    e.preventDefault();
    try {
      await apiCall("post", `/invoices/${selected.invoice_id}/payments`, {
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes || null,
      });
      toast.success("Pagamento registrado");
      setPaymentOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao registrar pagamento");
    }
  };

  const handleViewDetails = (inv) => {
    setSelected(inv);
    setDetailsOpen(true);
  };

  const handleDelete = async (id) => {
    if (!isMaster) {
      toast.error("Apenas usuários master podem excluir cobranças");
      return;
    }
    if (!confirm("Deseja excluir esta cobrança?")) return;
    try {
      await apiCall("delete", `/invoices/${id}`);
      toast.success("Cobrança excluída");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir");
    }
  };

  const handleSendNotification = async (invoice) => {
    const client = clients.find((c) => c.client_id === invoice.client_id);
    if (!client) return toast.error("Cliente não encontrado");
    try {
      const remaining = invoice.amount - (invoice.paid_amount || 0);
      const message = `Olá ${client.name}! 👋\n\nLembrando que você tem uma cobrança de *${formatCurrency(remaining)}* com vencimento em ${formatDate(invoice.due_date)}.\n\n${invoice.description || ""}\n\nQualquer dúvida estou à disposição. 💕`;
      const res = await apiCall("post", "/whatsapp/link", { phone: client.phone, message });
      window.open(res.data.link, "_blank");
      toast.success("WhatsApp aberto!");
    } catch (err) {
      toast.error("Erro ao gerar link do WhatsApp");
    }
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0);

  const formatDate = (date) => new Date(date).toLocaleDateString("pt-BR");
  const formatDateTime = (date) =>
    new Date(date).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

  // Summary
  const totals = invoices.reduce(
    (acc, i) => ({
      total: acc.total + i.amount,
      paid: acc.paid + (i.paid_amount || 0),
      pending: acc.pending + (i.amount - (i.paid_amount || 0)),
    }),
    { total: 0, paid: 0, pending: 0 }
  );

  return (
    <div className="space-y-6 lg:space-y-8" data-testid="invoices-page">
      <PageHeader overline="Financeiro" title="Cobranças" subtitle="Contas a receber e registro detalhado de pagamentos">
        <Button onClick={() => setOpen(true)} data-testid="add-invoice-button" disabled={clients.length === 0}>
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Nova Cobrança
        </Button>
      </PageHeader>

      {!isMaster && (
        <Card className="border border-accent/40 bg-accent/5 p-4 shadow-soft rounded-md">
          <div className="flex items-center gap-3 text-sm">
            <Crown className="h-4 w-4 flex-shrink-0" strokeWidth={2} style={{ color: "#CBA871" }} />
            <span className="text-muted-foreground">
              Você está visualizando cobranças. Para <span className="text-foreground font-medium">registrar pagamentos</span> ou <span className="text-foreground font-medium">excluir</span>, é necessário acesso master.
            </span>
          </div>
        </Card>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border border-border bg-card p-5 shadow-soft rounded-md" data-testid="invoices-total">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 font-heading">A Receber Total</div>
          <div className="text-2xl font-light tracking-tight font-heading">{formatCurrency(totals.total)}</div>
        </Card>
        <Card className="border border-border bg-card p-5 shadow-soft rounded-md" data-testid="invoices-paid">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 font-heading">Já Recebido</div>
          <div className="text-2xl font-light tracking-tight font-heading" style={{ color: "#166534" }}>
            {formatCurrency(totals.paid)}
          </div>
        </Card>
        <Card className="border border-border bg-card p-5 shadow-soft rounded-md" data-testid="invoices-pending">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 font-heading">Pendente</div>
          <div className="text-2xl font-light tracking-tight font-heading" style={{ color: "#CBA871" }}>
            {formatCurrency(totals.pending)}
          </div>
        </Card>
      </div>

      {clients.length === 0 && (
        <Card className="border border-border bg-card p-6 shadow-soft rounded-md">
          <p className="text-sm text-muted-foreground">Cadastre clientes primeiro para criar cobranças.</p>
        </Card>
      )}

      {/* Mobile cards / Desktop table */}
      <div className="lg:hidden space-y-3">
        {loading ? (
          <Card className="border border-border bg-card p-8 text-center text-muted-foreground shadow-soft rounded-md">
            Carregando...
          </Card>
        ) : invoices.length === 0 ? (
          <Card className="border border-border bg-card p-8 text-center text-muted-foreground shadow-soft rounded-md">
            Nenhuma cobrança registrada.
          </Card>
        ) : (
          invoices.map((inv) => {
            const remaining = inv.amount - (inv.paid_amount || 0);
            return (
              <Card key={inv.invoice_id} className="border border-border bg-card p-4 shadow-soft rounded-md" data-testid={`invoice-card-${inv.invoice_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-medium">{inv.client_name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Venc: {formatDate(inv.due_date)}</div>
                  </div>
                  <span
                    className="text-xs uppercase tracking-widest font-medium px-2 py-0.5 rounded"
                    style={{ background: STATUS_LABELS[inv.status]?.bg, color: STATUS_LABELS[inv.status]?.color }}
                  >
                    {STATUS_LABELS[inv.status]?.label}
                  </span>
                </div>
                {inv.description && <div className="text-xs text-muted-foreground mb-3 truncate">{inv.description}</div>}
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Total</div>
                    <div className="font-medium" style={{ color: "#CBA871" }}>{formatCurrency(inv.amount)}</div>
                  </div>
                  {inv.paid_amount > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground">Pago</div>
                      <div className="text-sm font-medium" style={{ color: "#166534" }}>{formatCurrency(inv.paid_amount)}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Resta</div>
                    <div className="font-medium">{formatCurrency(remaining)}</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => handleViewDetails(inv)} data-testid={`invoice-view-${inv.invoice_id}`}>
                    <Eye className="h-3 w-3" strokeWidth={1.5} />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleOpenPayment(inv)} disabled={!isMaster || inv.status === "pago"} data-testid={`invoice-pay-${inv.invoice_id}`}>
                    Pagar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleSendNotification(inv)} data-testid={`invoice-notify-${inv.invoice_id}`}>
                    <MessageCircle className="h-3 w-3" strokeWidth={1.5} />
                  </Button>
                </div>
              </Card>
            );
          })
        )}
      </div>

      <Card className="hidden lg:block border border-border bg-card shadow-soft rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/30 hover:bg-secondary/30">
              <TableHead className="text-xs uppercase tracking-widest font-medium">Cliente</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Descrição</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Vencimento</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Total</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Pago</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Resta</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Status</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest font-medium">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : invoices.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">Nenhuma cobrança registrada.</TableCell></TableRow>
            ) : (
              invoices.map((inv) => {
                const remaining = inv.amount - (inv.paid_amount || 0);
                return (
                  <TableRow key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`}>
                    <TableCell className="font-medium">{inv.client_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{inv.description || "—"}</TableCell>
                    <TableCell className="text-sm">{formatDate(inv.due_date)}</TableCell>
                    <TableCell className="font-medium" style={{ color: "#CBA871" }}>{formatCurrency(inv.amount)}</TableCell>
                    <TableCell style={{ color: "#166534" }}>{formatCurrency(inv.paid_amount || 0)}</TableCell>
                    <TableCell className="font-medium">{formatCurrency(remaining)}</TableCell>
                    <TableCell>
                      <span
                        className="text-xs uppercase tracking-widest font-medium px-2 py-1 rounded"
                        style={{ background: STATUS_LABELS[inv.status]?.bg, color: STATUS_LABELS[inv.status]?.color }}
                      >
                        {STATUS_LABELS[inv.status]?.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => handleViewDetails(inv)} title="Detalhes" data-testid={`invoice-view-row-${inv.invoice_id}`}>
                        <Eye className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenPayment(inv)}
                        disabled={!isMaster || inv.status === "pago"}
                        title={isMaster ? "Registrar pagamento" : "Apenas master"}
                        data-testid={`invoice-pay-row-${inv.invoice_id}`}
                      >
                        <Banknote className="h-4 w-4" strokeWidth={1.5} style={{ color: isMaster && inv.status !== "pago" ? "#166534" : undefined }} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleSendNotification(inv)} title="Notificar cliente" data-testid={`invoice-notify-row-${inv.invoice_id}`}>
                        <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                      {isMaster && (
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(inv.invoice_id)} title="Excluir (master)" data-testid={`invoice-delete-row-${inv.invoice_id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* New Invoice Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">Nova Cobrança</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Cliente</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })} required>
                <SelectTrigger data-testid="invoice-client-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.client_id} value={c.client_id} data-testid={`invoice-client-option-${c.client_id}`}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Valor (R$)</Label>
                <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="invoice-amount-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Vencimento</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} required data-testid="invoice-due-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Descrição</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="invoice-description-input" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="invoice-save-button">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl flex items-center gap-2">
              <Crown className="h-5 w-5" strokeWidth={2} style={{ color: "#CBA871" }} />
              Registrar Pagamento
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <form onSubmit={handleRegisterPayment} className="space-y-4 mt-4">
              <div className="p-3 bg-secondary/50 rounded-md text-sm">
                <div className="font-medium">{selected.client_name}</div>
                <div className="flex justify-between mt-1 text-muted-foreground">
                  <span>Total: {formatCurrency(selected.amount)}</span>
                  <span>Já pago: {formatCurrency(selected.paid_amount || 0)}</span>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Valor (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={selected.amount - (selected.paid_amount || 0)}
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  required
                  data-testid="payment-amount-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Forma de Pagamento</Label>
                <Select
                  value={paymentForm.payment_method}
                  onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}
                >
                  <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((pm) => (
                      <SelectItem key={pm.value} value={pm.value} data-testid={`payment-method-${pm.value}`}>
                        <div className="flex items-center gap-2">
                          <pm.icon className="h-4 w-4" strokeWidth={1.5} />
                          {pm.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Observações</Label>
                <Input
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  placeholder="Ex: Parcela 1 de 3"
                  data-testid="payment-notes-input"
                />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setPaymentOpen(false)}>Cancelar</Button>
                <Button type="submit" data-testid="payment-save-button">Confirmar Pagamento</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">Detalhes da Cobrança</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-secondary/50 rounded-md">
                <div className="text-lg font-medium mb-1">{selected.client_name}</div>
                {selected.description && <div className="text-sm text-muted-foreground">{selected.description}</div>}
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Total</div>
                  <div className="font-medium" style={{ color: "#CBA871" }}>{formatCurrency(selected.amount)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Pago</div>
                  <div className="font-medium" style={{ color: "#166534" }}>{formatCurrency(selected.paid_amount || 0)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Resta</div>
                  <div className="font-medium">{formatCurrency(selected.amount - (selected.paid_amount || 0))}</div>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Histórico de Pagamentos</div>
                {(selected.payments || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">Nenhum pagamento registrado.</p>
                ) : (
                  <div className="space-y-2">
                    {selected.payments.map((p, idx) => (
                      <div key={idx} className="border border-border rounded-md p-3 text-sm" data-testid={`payment-history-${idx}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium" style={{ color: "#166534" }}>
                            {formatCurrency(p.amount)}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded bg-secondary text-foreground">
                            {PM_LABEL[p.payment_method] || p.payment_method}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{formatDateTime(p.paid_at)}</span>
                          <span className="flex items-center gap-1">
                            <Crown className="h-3 w-3" strokeWidth={2} style={{ color: "#CBA871" }} />
                            {p.received_by_name}
                          </span>
                        </div>
                        {p.notes && <div className="text-xs mt-1 italic">{p.notes}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <DialogFooter>
                {isMaster && selected.status !== "pago" && (
                  <Button onClick={() => { setDetailsOpen(false); handleOpenPayment(selected); }} data-testid="invoice-details-pay-button">
                    <Banknote className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Registrar Pagamento
                  </Button>
                )}
                <Button variant="outline" onClick={() => setDetailsOpen(false)}>Fechar</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
